// "Save image": the finished round as a 1920 x 1080 PNG (both builds, names,
// the verdict and the game link), for clips and posts.

import type { AuctionEngine } from '../game/engine';
import type { Offset } from '../game/geom';
import { drawBuild, neededImages } from '../game/render';
import type { RoundSetup } from '../game/themes';
import { loadImage, type ThemeVisual } from '../game/visual';
import { PLAYER_COLORS, ORANGE } from '../theme';
import { PLAY_HOST } from '../components/ui';

const W = 1920;
const H = 1080;
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const next = line ? `${line} ${w}` : w;
    if (ctx.measureText(next).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else line = next;
  }
  if (line) lines.push(line);
  return lines;
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export async function exportResult(o: {
  engine: AuctionEngine;
  round: RoundSetup;
  visual: ThemeVisual | null;
  names: string[];
  moved: Map<number, Offset>[];
  winner: number | null;
  headline: string | null;
  verdict: string | null;
  scores: number[] | null;
  left: (amount: number) => string;
}): Promise<void> {
  const c = document.createElement('canvas');
  c.width = W;
  c.height = H;
  const ctx = c.getContext('2d')!;
  const bg = ctx.createRadialGradient(W / 2, -200, 100, W / 2, H / 2, 1400);
  bg.addColorStop(0, '#3a2600');
  bg.addColorStop(1, '#050505');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  ctx.textAlign = 'center';
  ctx.fillStyle = '#fff';
  ctx.font = `900 54px ${FONT}`;
  ctx.fillText(o.round.theme.theme, W / 2, 86);

  const side = 660;
  const top = 170;
  const xs = [70, W - 70 - side];
  for (let p = 0; p < 2; p++) {
    const x = xs[p];
    ctx.save();
    roundRect(ctx, x, top, side, side, 28);
    ctx.clip();
    if (o.visual) {
      const won = o.engine.builds[p].map((w) => o.round.sources[w.itemIndex]);
      const d = { visual: o.visual, won, moved: o.moved[p] };
      const imgs = new Map<string, HTMLImageElement>();
      await Promise.all(
        neededImages(d).map((u) =>
          loadImage(u)
            .then((i) => imgs.set(u, i))
            .catch(() => null),
        ),
      );
      const off = document.createElement('canvas');
      off.width = side;
      off.height = side;
      drawBuild(off.getContext('2d')!, side, d, imgs);
      ctx.drawImage(off, x, top);
    } else {
      ctx.fillStyle = '#1c1c1e';
      ctx.fillRect(x, top, side, side);
      ctx.textAlign = 'left';
      ctx.font = `800 38px ${FONT}`;
      o.engine.builds[p].forEach((w, i) => {
        ctx.fillStyle = PLAYER_COLORS[p];
        ctx.fillText(`${i + 1}.`, x + 40, top + 80 + i * 62);
        ctx.fillStyle = '#fff';
        ctx.fillText(w.name, x + 110, top + 80 + i * 62, side - 150);
      });
      ctx.textAlign = 'center';
    }
    ctx.restore();
    if (o.winner === p) {
      ctx.lineWidth = 10;
      ctx.strokeStyle = PLAYER_COLORS[p];
      roundRect(ctx, x - 5, top - 5, side + 10, side + 10, 32);
      ctx.stroke();
    }
    ctx.fillStyle = PLAYER_COLORS[p];
    ctx.font = `900 48px ${FONT}`;
    const label = `${o.winner === p ? '👑 ' : ''}${o.names[p]}`;
    ctx.fillText(label, x + side / 2, top + side + 66, side);
    ctx.fillStyle = '#bbb';
    ctx.font = `700 32px ${FONT}`;
    const score = o.scores ? `${o.scores[p].toFixed(1)}  ·  ` : '';
    ctx.fillText(`${score}${o.left(o.engine.wallets[p])}`, x + side / 2, top + side + 112);
  }

  // Middle column: headline and verdict.
  const mid = W / 2;
  const colW = W - 2 * (70 + side) - 60;
  if (o.headline) {
    ctx.fillStyle = o.winner === null ? '#fff' : PLAYER_COLORS[o.winner];
    ctx.font = `900 46px ${FONT}`;
    wrap(ctx, o.headline, colW).forEach((l, i) => ctx.fillText(l, mid, 320 + i * 54));
  } else {
    ctx.fillStyle = ORANGE;
    ctx.font = `900 120px ${FONT}`;
    ctx.fillText('VS', mid, 560);
  }
  if (o.verdict) {
    ctx.fillStyle = '#eee';
    ctx.font = `600 28px ${FONT}`;
    wrap(ctx, `“${o.verdict}”`, colW)
      .slice(0, 12)
      .forEach((l, i) => ctx.fillText(l, mid, 440 + i * 38));
  }

  ctx.fillStyle = ORANGE;
  ctx.font = `800 30px ${FONT}`;
  ctx.fillText(`$20 Challenge · ${PLAY_HOST}`, mid, H - 40);

  const blob = await new Promise<Blob | null>((r) => c.toBlob(r, 'image/png'));
  if (!blob) throw new Error('png');
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `20-challenge-${o.round.theme.id}.png`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}
